import { Sort } from '@wconcept/wconcept-common/models/display/search';
import type { ChangeEvent, ReactNode } from 'react';
import Button from '@wconcept/wconcept-ui-renewal/src/components/atoms/Button';
import SortingBtn from '@wconcept/wconcept-ui-renewal/src/components/molecules/_btn/SortingBtn';
import SortingMenuList from '@wconcept/wconcept-ui-renewal/src/components/organisms/_list/SortingMenuList';
import SortingBar from '@wconcept/wconcept-ui-renewal/components/molecules/SortingBar';
import Checkbox from '@wconcept/wconcept-ui-renewal/components/atoms/Checkbox';
import { useCallback, useContext } from 'react';
import SortingModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/SortingModal';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/src/contexts/ModalsContext';
import type { LpProductGroupType } from '@apis/renewal/lpProduct';

interface IProductSortBar {
  isMobile: boolean;
  sorts: Sort[] | undefined;
  value?: string;
  isSoldOut?: boolean;
  isPreOrder?: boolean;
  isFreeDelivery?: boolean;
  useSoldOut?: boolean;
  usePreOrder?: boolean;
  useFreeDelivery?: boolean;
  isNativeAppBoth?: boolean;
  toggleItem?: ReactNode;
  onSoldoutChange?: (soldOut: boolean) => void;
  onChangePreOrderOrFreeDelivery?: (
    checked: boolean,
    code: string,
    groupType: LpProductGroupType
  ) => void;
  onChangeSort?: (sort: Sort) => void;
}
interface SortOptions {
  text: string;
  value: string;
  checked: boolean;
  useSoldOut?: boolean;
}
function ProductSortBar({
  isMobile,
  sorts,
  value,
  isSoldOut,
  isPreOrder,
  isFreeDelivery,
  useSoldOut,
  usePreOrder,
  useFreeDelivery,
  isNativeAppBoth,
  toggleItem,
  onSoldoutChange,
  onChangePreOrderOrFreeDelivery,
  onChangeSort,
}: IProductSortBar) {
  const { openModal } = useContext(ModalsContext);

  const getIsGnbType = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('gnbType') === 'Y';
  }, []);

  const getSortingBarItems = useCallback(
    () =>
      !isMobile ? (
        <SortingMenuList>
          {sorts?.map(v => (
            <Button
              key={v.code}
              className={`item${v.code === value ? ' active' : ''}`}
              onClick={() => {
                onChangeSort?.(v);
              }}
            >
              {v.name}
              {v.tooltip}
            </Button>
          ))}
        </SortingMenuList>
      ) : (
        <SortingBtn
          text={sorts?.find(v => v.code === value)?.name || ''}
          onClick={() => {
            if (isNativeAppBoth && getIsGnbType()) {
              document.location.href = 'wconceptkr://actionBarOff';
            }
            openModal<
              { options: SortOptions[] | undefined },
              {
                text: string;
                value: string;
                checked: boolean;
              }[]
            >(SortingModal, {
              value: {
                options: sorts?.map(v => ({
                  text: v.name,
                  value: v.code,
                  checked: v.code === value,
                  tooltip: v.tooltip,
                })),
              },
              onClose: () => {
                if (isNativeAppBoth && getIsGnbType()) {
                  document.location.href = 'wconceptkr://actionBarOn';
                }
              },
              onSubmit: data => {
                const chk = data.filter(v => v.checked === true)[0];
                onChangeSort?.({ code: chk.value, name: chk.text });
                if (isNativeAppBoth && getIsGnbType()) {
                  document.location.href = 'wconceptkr://actionBarOn';
                }
              },
            });
          }}
        />
      ),
    [
      getIsGnbType,
      isMobile,
      isNativeAppBoth,
      onChangeSort,
      openModal,
      sorts,
      value,
    ]
  );

  return (
    <SortingBar
      chkItem={[
        useSoldOut && (
          <Checkbox
            key="chk3"
            label="품절제외"
            value="chk3"
            checked={isSoldOut}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onSoldoutChange?.(e.target.checked);
            }}
          />
        ),
        usePreOrder && (
          <Checkbox
            key="chk1"
            label="예약제외"
            value="chk1"
            checked={isPreOrder}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onChangePreOrderOrFreeDelivery?.(
                e.target.checked,
                'excludePreOrder',
                'productType'
              );
            }}
          />
        ),
        useFreeDelivery && (
          <Checkbox
            key="chk2"
            label="무료배송"
            value="chk2"
            checked={isFreeDelivery}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onChangePreOrderOrFreeDelivery?.(
                e.target.checked,
                'freeDelivery',
                'benefit'
              );
            }}
          />
        ),
      ]}
      items={getSortingBarItems()}
      toggleItem={toggleItem}
    />
  );
}

export default ProductSortBar;
